import React, { useState } from 'react';
import { X, ShieldCheck, Loader2, AlertTriangle } from 'lucide-react';
import { DbsCheck, DbsCheckType, InitiateDbsCheckRequest } from '../../services/api';

interface InitiateDbsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (payload: InitiateDbsCheckRequest) => Promise<void>;
    check: DbsCheck;
    orgName: string;
    currentUserName?: string;
}

const PURPOSE_OPTIONS = ['Employment', 'Personal Interest', 'Other'];
const SECTOR_OPTIONS: { label: string; value: string }[] = [
    { label: 'Drivers / Transport', value: 'DRIVERS' },
    { label: 'Transportation & Storage', value: 'TRANSPORTATIONANDSTORAGE' },
    { label: 'Construction', value: 'CONSTRUCTION' },
    { label: 'Manufacturing', value: 'MANUFACTURING' },
    { label: 'Retail', value: 'RETAIL' },
    { label: 'Healthcare (NHS)', value: 'NHS' },
    { label: 'Healthcare (Private)', value: 'PRIVATEHEALTHCARE' },
    { label: 'Social Care', value: 'SOCIALCARE' },
    { label: 'Finance & Insurance', value: 'FINANCIALANDINSURANCE' },
    { label: 'Information & Communication', value: 'INFORMATIONANDCOMMUNICATION' },
    { label: 'Education (Primary)', value: 'PRIMARYEDUCATION' },
    { label: 'Education (Secondary)', value: 'SECONDARYEDUCATION' },
    { label: 'Education (Further)', value: 'FURTHEREDUCATION' },
    { label: 'Education (Higher)', value: 'HIGHEREDUCATION' },
    { label: 'Local Government', value: 'LOCALGOVERNMENT' },
    { label: 'Central Government', value: 'CENTRALGOVERNMENT' },
    { label: 'Voluntary / Charity', value: 'VOLUNTARYCHARITY' },
    { label: 'Law Enforcement & Security', value: 'LAWENFORCEMENTANDSECURITY' },
    { label: 'Professional & Technical', value: 'PROFESSIONALTECHNICAL' },
    { label: 'Administrative & Support', value: 'ADMINISTRATIVEANDSUPPORT' },
    { label: 'Agriculture, Forestry & Fishing', value: 'AGRICULTUREFORESTRYANDFISHING' },
    { label: 'Energy & Air Conditioning', value: 'ENERGYANDAIRCONDITIONING' },
    { label: 'Arts & Entertainment', value: 'ARTSANDENTERTAINMENT' },
    { label: 'Leisure, Sport & Tourism', value: 'LEISURESPORTANDTOURISM' },
    { label: 'Recruitment & HR', value: 'RECRUITMENTANDHR' },
    { label: 'Other (Public Sector)', value: 'PUBLICSECTOROTHER' },
];
const WORKFORCE_OPTIONS = ['Adult Workforce', 'Child Workforce', 'Child and Adult Workforce', 'Other Workforce'];

function gpg45Label(state: 1 | 3 | 4): string {
    if (state === 3) return 'Digital: address verified';
    if (state === 4) return 'Digital: address not verified';
    return 'Manual route';
}

function isEnhanced(checkType: DbsCheckType) { return checkType === 'enhanced'; }
function isStdOrEnh(checkType: DbsCheckType) { return checkType === 'standard' || checkType === 'enhanced'; }

const InitiateDbsModal: React.FC<InitiateDbsModalProps> = ({ isOpen, onClose, onConfirm, check, orgName, currentUserName = '' }) => {
    const state = check.gpg45State ?? 3;
    const checkType = check.checkType;

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Common fields
    const [purpose, setPurpose] = useState('Employment');
    const [sector, setSector] = useState('DRIVERS');
    const [employerName, setEmployerName] = useState(orgName);
    const [other, setOther] = useState('');

    // State 4 field
    const [addressChecked, setAddressChecked] = useState(false);

    // State 1 fields
    const [docsChecked, setDocsChecked] = useState(false);
    const [dobChecked, setDobChecked] = useState(false);
    const [evidenceBy] = useState(currentUserName);
    const [evidenceDate, setEvidenceDate] = useState('');
    const [selfDeclaration, setSelfDeclaration] = useState(false);

    // Standard/Enhanced fields
    const [position, setPosition] = useState('Driver');
    const [workforce, setWorkforce] = useState('Adult Workforce');
    const [adults, setAdults] = useState(false);
    const [children, setChildren] = useState(false);
    const [vulnerable, setVulnerable] = useState(false);
    const [volunteer, setVolunteer] = useState(false);
    const [additionalInfo, setAdditionalInfo] = useState('');

    const showBarredAdults = isEnhanced(checkType) &&
        (workforce === 'Adult Workforce' || workforce === 'Child and Adult Workforce' || workforce === 'Other Workforce');
    const showBarredChildren = isEnhanced(checkType) &&
        (workforce === 'Child Workforce' || workforce === 'Child and Adult Workforce' || workforce === 'Other Workforce');
    const showVulnerable = isEnhanced(checkType);

    const isValid = () => {
        if (!employerName.trim()) return false;
        if (purpose === 'Other' && !other.trim()) return false;
        if (state !== 3 && (!addressChecked || !docsChecked || !dobChecked)) return false;
        if (state !== 3) {
            if (!evidenceBy.trim() || !evidenceDate) return false;
            if (!selfDeclaration) return false;
        }
        if (isStdOrEnh(checkType) && !position.trim()) return false;
        return true;
    };

    const handleSubmit = async () => {
        if (!isValid() || isSubmitting) return;
        setIsSubmitting(true);
        try {
            const payload: InitiateDbsCheckRequest = {
                employerName,
                purposeOfCheck: purpose,
                employmentSector: purpose === 'Employment' ? sector : undefined,
                applicationConsent: true,
                other: purpose === 'Other' ? other : undefined,
                candidateAddressChecked: state !== 3 ? addressChecked : undefined,
                candidateOriginalDocumentsChecked: state !== 3 ? docsChecked : undefined,
                candidateDateOfBirthChecked: state !== 3 ? dobChecked : undefined,
                evidenceCheckedBy: state !== 3 ? evidenceBy : undefined,
                evidenceCheckedDate: state !== 3 ? evidenceDate : undefined,
                selfDeclarationCheck: state !== 3 ? selfDeclaration : undefined,
                applicationType: isStdOrEnh(checkType) ? (checkType === 'enhanced' ? 'Enhanced' : 'Standard') : undefined,
                positionAppliedFor: isStdOrEnh(checkType) ? position : undefined,
                workforce: isStdOrEnh(checkType) ? workforce : undefined,
                workingWithAdults: showBarredAdults ? adults : false,
                workingWithChildren: showBarredChildren ? children : false,
                vulnerableGroupsAtPrivateHomeAddress: showVulnerable ? vulnerable : false,
                volunteer: isStdOrEnh(checkType) ? volunteer : undefined,
                additionalInformation: isStdOrEnh(checkType) ? additionalInfo : undefined,
            };
            await onConfirm(payload);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const cardClass = 'bg-gray-50/80 rounded-lg border border-gray-100 p-4 space-y-3';
    const labelClass = 'block text-xs font-medium text-gray-600 mb-1';
    const inputClass = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-400';
    const selectClass = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-400 bg-white';
    const checkboxRowClass = 'flex items-start gap-3';
    const checkboxLabelClass = 'text-sm text-gray-700 leading-snug';

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full flex flex-col" style={{ maxHeight: '85vh' }}>

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.1)' }}>
                            <ShieldCheck className="w-5 h-5" style={{ color: '#69BE28' }} />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-gray-900">Initiate DBS Check</h3>
                            <p className="text-xs text-gray-500 capitalize">{check.checkType} DBS, {gpg45Label(state as 1 | 3 | 4)}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">

                    {/* State indicator */}
                    {state === 1 && (
                        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-800">
                                The driver's identity could not be verified digitally. Original documents must be checked in person before proceeding.
                            </p>
                        </div>
                    )}
                    {state === 4 && (
                        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-800">
                                Address verification was not completed digitally. Evidence of the driver's address must be obtained within the last 90 days.
                            </p>
                        </div>
                    )}

                    {/* Purpose + Employer */}
                    <div className={cardClass}>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Check Details</h4>

                        <div>
                            <label className={labelClass}>Purpose of check</label>
                            <select className={selectClass} value={purpose} onChange={e => setPurpose(e.target.value)}>
                                {PURPOSE_OPTIONS.map(p => <option key={p}>{p}</option>)}
                            </select>
                        </div>

                        {purpose === 'Employment' && (
                            <div>
                                <label className={labelClass}>Employment sector</label>
                                <select className={selectClass} value={sector} onChange={e => setSector(e.target.value)}>
                                    {SECTOR_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                            </div>
                        )}

                        {purpose === 'Other' && (
                            <div>
                                <label className={labelClass}>Please specify</label>
                                <input className={inputClass} value={other} onChange={e => setOther(e.target.value)} placeholder="Reason for check" />
                            </div>
                        )}

                        <div>
                            <label className={labelClass}>Employer name</label>
                            <input className={inputClass} value={employerName} onChange={e => setEmployerName(e.target.value)} />
                        </div>
                    </div>

                    {/* Standard / Enhanced fields */}
                    {isStdOrEnh(checkType) && (
                        <div className={cardClass}>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Role Details</h4>

                            <div>
                                <label className={labelClass}>Position applied for</label>
                                <input className={inputClass} value={position} onChange={e => setPosition(e.target.value)} />
                            </div>

                            <div>
                                <label className={labelClass}>Workforce</label>
                                <select className={selectClass} value={workforce} onChange={e => setWorkforce(e.target.value)}>
                                    {WORKFORCE_OPTIONS.map(w => <option key={w}>{w}</option>)}
                                </select>
                            </div>

                            {showBarredAdults && (
                                <div className={checkboxRowClass}>
                                    <input type="checkbox" id="adults" checked={adults} onChange={e => setAdults(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300" style={{ accentColor: '#69BE28' }} />
                                    <label htmlFor="adults" className={checkboxLabelClass}>Check against the adults' barred list</label>
                                </div>
                            )}

                            {showBarredChildren && (
                                <div className={checkboxRowClass}>
                                    <input type="checkbox" id="children" checked={children} onChange={e => setChildren(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300" style={{ accentColor: '#69BE28' }} />
                                    <label htmlFor="children" className={checkboxLabelClass}>Check against the children's barred list</label>
                                </div>
                            )}

                            {showVulnerable && (
                                <div className={checkboxRowClass}>
                                    <input type="checkbox" id="vulnerable" checked={vulnerable} onChange={e => setVulnerable(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300" style={{ accentColor: '#69BE28' }} />
                                    <label htmlFor="vulnerable" className={checkboxLabelClass}>Role involves access to vulnerable groups at a private address</label>
                                </div>
                            )}

                            <div className={checkboxRowClass}>
                                <input type="checkbox" id="volunteer" checked={volunteer} onChange={e => setVolunteer(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300" style={{ accentColor: '#69BE28' }} />
                                <label htmlFor="volunteer" className={checkboxLabelClass}>Volunteer role (unpaid)</label>
                            </div>

                            <div>
                                <label className={labelClass}>Additional information <span className="text-gray-400 font-normal">(optional)</span></label>
                                <textarea
                                    className={`${inputClass} resize-none`}
                                    rows={2}
                                    value={additionalInfo}
                                    onChange={e => setAdditionalInfo(e.target.value)}
                                    placeholder="Any relevant additional context"
                                />
                            </div>
                        </div>
                    )}

                    {/* State 4 / State 1 - address + document evidence */}
                    {state !== 3 && (
                        <div className={cardClass}>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Document &amp; Address Confirmation</h4>
                            <div className={checkboxRowClass}>
                                <input type="checkbox" id="docsChecked" checked={docsChecked} onChange={e => setDocsChecked(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300" style={{ accentColor: '#69BE28' }} />
                                <label htmlFor="docsChecked" className={checkboxLabelClass}>The candidate's original identity documents have been checked in person</label>
                            </div>
                            <div className={checkboxRowClass}>
                                <input type="checkbox" id="dobChecked" checked={dobChecked} onChange={e => setDobChecked(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300" style={{ accentColor: '#69BE28' }} />
                                <label htmlFor="dobChecked" className={checkboxLabelClass}>The candidate's date of birth has been verified against their documents</label>
                            </div>
                            <div className={checkboxRowClass}>
                                <input type="checkbox" id="addressChecked" checked={addressChecked} onChange={e => setAddressChecked(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300" style={{ accentColor: '#69BE28' }} />
                                <label htmlFor="addressChecked" className={checkboxLabelClass}>
                                    Evidence of the candidate's current address has been obtained within the last 90 days
                                </label>
                            </div>
                        </div>
                    )}

                    {/* State 4 / State 1 - evidence record */}
                    {state !== 3 && (
                        <div className={cardClass}>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Evidence Record</h4>

                            <div>
                                <label className={labelClass}>Evidence checked by</label>
                                <input className={`${inputClass} bg-gray-100 cursor-not-allowed`} value={evidenceBy} readOnly />
                            </div>

                            <div>
                                <label className={labelClass}>Date evidence was checked</label>
                                <input type="date" className={inputClass} value={evidenceDate} onChange={e => setEvidenceDate(e.target.value)} />
                            </div>

                            <div className={checkboxRowClass}>
                                <input type="checkbox" id="selfDeclaration" checked={selfDeclaration} onChange={e => setSelfDeclaration(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300" style={{ accentColor: '#69BE28' }} />
                                <label htmlFor="selfDeclaration" className={checkboxLabelClass}>
                                    I confirm that the original documents will be retained for 24 months and are available for inspection if required
                                </label>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !isValid()}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {isSubmitting ? 'Submitting...' : 'Initiate DBS Check'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InitiateDbsModal;
